import { createApi } from '@reduxjs/toolkit/query/react'
import { BACKEND_API_URL, urlApi } from '../consts/api'

import { notificationsTag } from './notifications'
import { recommendationsApi, recommendationsSummaryTag, recommendationsTag } from './recommendations'
import { teamsTag } from './teams'
import { usersTag } from './users'
import { extractResponseData, extractResponseError, fetchBaseQueryWithToken } from '../utils/api'
import { getMediaSourceUrl } from '../utils/media'
import {
    ILogin,
    ILoginResponse,
    IRequestEmailCodeRequest,
    IRegisterRequest,
    IRegisterResponse,
  TBlockedTeamItem,
  TBlockedUserItem,
    IUpdateUserBody,
    IVerifyEmailCodeRequest,
    IVerifyEmailCodeResponse,
    TUser
} from '../api/types'

export const profileTag = 'profileTag'
export const blockingSettingsTag = 'blockingSettingsTag'
type UpdateUserProfileBody = Partial<IUpdateUserBody> | FormData
type RequestEmailCodeQueryResult = { data: unknown } | { error: any }
const requestEmailCodeInflight = new Map<string, Promise<RequestEmailCodeQueryResult>>()

function parseProfileResponse(response: unknown): TUser {
    const u = extractResponseData(response) as TUser & { deleted?: boolean }

    if (u.deleted === true) {
        return {
            id: Number(u.id),
            email: String(u.email ?? ''),
            deleted: true,
            deleted_at: u.deleted_at ?? null,
        } as TUser
    }

    return {
        ...u,
        avatar: getMediaSourceUrl((u as { avatar?: unknown }).avatar),
        is_platform_staff: (u as { is_platform_staff?: unknown }).is_platform_staff === true,
    }
}

function parseBlockedUsersResponse(response: unknown): TBlockedUserItem[] {
    const data = extractResponseData(response)
    if (!Array.isArray(data)) {
        return []
    }

    return data.map((item) => {
        const row = (item ?? {}) as Record<string, unknown>
        return {
            id: Number(row.id ?? 0),
            slug: String(row.slug ?? ''),
            shortname: String(row.shortname ?? ''),
            fullname: row.fullname != null ? String(row.fullname) : null,
            status: String(row.status ?? ''),
            avatar: getMediaSourceUrl(row.avatar),
            blocked_at: row.blocked_at != null ? String(row.blocked_at) : null,
        }
    })
}

function parseBlockedTeamsResponse(response: unknown): TBlockedTeamItem[] {
    const data = extractResponseData(response)
    if (!Array.isArray(data)) {
        return []
    }

    return data.map((item) => {
        const row = (item ?? {}) as Record<string, unknown>
        return {
            id: Number(row.id ?? 0),
            name: String(row.name ?? ''),
            slug: String(row.slug ?? ''),
            location: row.location != null ? String(row.location) : null,
            image: getMediaSourceUrl(row.image),
            blocked_at: row.blocked_at != null ? String(row.blocked_at) : null,
        }
    })
}

export const profileApi = createApi({
    reducerPath: 'profileApi',
    baseQuery: fetchBaseQueryWithToken(BACKEND_API_URL),
    tagTypes: [profileTag, blockingSettingsTag, usersTag, teamsTag, notificationsTag],
    endpoints: (builder) => ({
        getProfile: builder.query<TUser, void>({
            query: () => ({
                url: `/${urlApi.me}`,
            }),
            transformResponse: (response: unknown) => parseProfileResponse(response),
            transformErrorResponse: extractResponseError,
            providesTags: [profileTag],
        }),

        updateUserProfile: builder.mutation<TUser, { userId: number; body: UpdateUserProfileBody }>({
            query: ({ userId, body }) => ({
                url: `users/${userId}/update`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: [profileTag],
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled
                    dispatch(recommendationsApi.util.invalidateTags([recommendationsTag, recommendationsSummaryTag]))
                } catch {
                    // no-op
                }
            },
            transformResponse: (response: unknown) => {
                const u = extractResponseData(response) as TUser
                return {
                    ...u,
                    avatar: getMediaSourceUrl((u as { avatar?: unknown }).avatar),
                }
            },
            transformErrorResponse: extractResponseError,
        }),

        uploadMyAvatar: builder.mutation<unknown, File>({
            query: (file) => {
                const formData = new FormData()
                formData.append('image', file)
                return {
                    url: `users/me/avatar`,
                    method: 'POST',
                    body: formData,
                }
            },
            invalidatesTags: [profileTag],
            transformErrorResponse: extractResponseError,
        }),

        deleteMyAvatar: builder.mutation<unknown, void>({
            query: () => ({
                url: `users/me/avatar`,
                method: 'DELETE',
            }),
            invalidatesTags: [profileTag],
            transformErrorResponse: extractResponseError,
        }),

        restoreMyAccount: builder.mutation<TUser, void>({
            query: () => ({
                url: `/${urlApi.users}/me/restore`,
                method: 'POST',
            }),
            transformResponse: (response: unknown) => parseProfileResponse(response),
            transformErrorResponse: extractResponseError,
            invalidatesTags: [profileTag],
        }),

        getBlockedUsers: builder.query<TBlockedUserItem[], void>({
            query: () => ({
                url: `/users/me/blocked-users`,
            }),
            transformResponse: (response: unknown) => parseBlockedUsersResponse(response),
            transformErrorResponse: extractResponseError,
            providesTags: [blockingSettingsTag],
        }),

        blockUser: builder.mutation<TBlockedUserItem, { userId: number }>({
            query: ({ userId }) => ({
                url: `/users/me/blocked-users`,
                method: 'POST',
                body: { user_id: userId },
            }),
            transformResponse: (response: unknown) => parseBlockedUsersResponse({ data: [extractResponseData(response)] })[0],
            transformErrorResponse: extractResponseError,
            invalidatesTags: [blockingSettingsTag, usersTag, notificationsTag],
        }),

        unblockUser: builder.mutation<void, { userId: number }>({
            query: ({ userId }) => ({
                url: `/users/me/blocked-users/${userId}`,
                method: 'DELETE',
            }),
            transformResponse: () => undefined,
            transformErrorResponse: extractResponseError,
            invalidatesTags: [blockingSettingsTag, usersTag, notificationsTag],
        }),

        getBlockedTeams: builder.query<TBlockedTeamItem[], void>({
            query: () => ({
                url: `/users/me/blocked-teams`,
            }),
            transformResponse: (response: unknown) => parseBlockedTeamsResponse(response),
            transformErrorResponse: extractResponseError,
            providesTags: [blockingSettingsTag],
        }),

        blockTeam: builder.mutation<TBlockedTeamItem, { teamId: number }>({
            query: ({ teamId }) => ({
                url: `/users/me/blocked-teams`,
                method: 'POST',
                body: { team_id: teamId },
            }),
            transformResponse: (response: unknown) => parseBlockedTeamsResponse({ data: [extractResponseData(response)] })[0],
            transformErrorResponse: extractResponseError,
            invalidatesTags: [blockingSettingsTag, teamsTag, notificationsTag],
        }),

        unblockTeam: builder.mutation<void, { teamId: number }>({
            query: ({ teamId }) => ({
                url: `/users/me/blocked-teams/${teamId}`,
                method: 'DELETE',
            }),
            transformResponse: () => undefined,
            transformErrorResponse: extractResponseError,
            invalidatesTags: [blockingSettingsTag, teamsTag, notificationsTag],
        }),
        
        loginUser: builder.mutation<ILoginResponse, ILogin>({
            query: ({ login, password }) => ({
                url: `/${urlApi.login}`,
                method: 'POST',
                body: {
                    login,
                    password,
                },
            }),
            invalidatesTags: [profileTag],
        }),

        requestEmailCode: builder.mutation<unknown, IRequestEmailCodeRequest>({
            async queryFn(
                { email }: IRequestEmailCodeRequest,
                _api: unknown,
                _extraOptions: unknown,
                baseQuery: (arg: unknown) => Promise<{ data: unknown } | { error: unknown }>
            ) {
                const normalizedEmail = String(email ?? '').trim().toLowerCase()
                if (!normalizedEmail) {
                    return { error: { status: 400, data: { message: 'Email is required' } } as any }
                }

                const inFlight = requestEmailCodeInflight.get(normalizedEmail)
                if (inFlight) {
                    return await inFlight
                }

                const requestPromise = (async () => {
                    const response = await baseQuery({
                        url: `/${urlApi.requestEmailCode}`,
                        method: 'POST',
                        body: { email: normalizedEmail },
                    })

                    if ('error' in response && response.error) {
                        return { error: extractResponseError(response.error) }
                    }

                    return { data: extractResponseData((response as any).data) }
                })() as Promise<RequestEmailCodeQueryResult>

                requestEmailCodeInflight.set(normalizedEmail, requestPromise)

                try {
                    return await requestPromise
                } finally {
                    requestEmailCodeInflight.delete(normalizedEmail)
                }
            },
        }),

        verifyEmailCode: builder.mutation<IVerifyEmailCodeResponse, IVerifyEmailCodeRequest>({
            query: ({ email, code }) => ({
                url: `/${urlApi.verifyEmailCode}`,
                method: 'POST',
                body: { email, code },
            }),
            invalidatesTags: [profileTag],
            transformResponse: (response: unknown) => extractResponseData(response) as IVerifyEmailCodeResponse,
            transformErrorResponse: extractResponseError,
        }),

        registerUser: builder.mutation<IRegisterResponse, IRegisterRequest>({
            query: (body) => ({
                url: `/${urlApi.register}`,
                method: 'POST',
                body,
            }),
            invalidatesTags: [profileTag],
            transformResponse: (response: unknown) => extractResponseData(response) as IRegisterResponse,
            transformErrorResponse: extractResponseError,
        }),
    })
})

export const {
    useGetProfileQuery,
    useGetBlockedUsersQuery,
    useGetBlockedTeamsQuery,
    useBlockUserMutation,
    useUnblockUserMutation,
    useBlockTeamMutation,
    useUnblockTeamMutation,
    useLoginUserMutation,
    useRequestEmailCodeMutation,
    useVerifyEmailCodeMutation,
    useRegisterUserMutation,
    useUpdateUserProfileMutation,
    useUploadMyAvatarMutation,
    useDeleteMyAvatarMutation,
    useRestoreMyAccountMutation,
} = profileApi
